import React, { useMemo } from 'react';
import { useCardPreviewWithPhoto } from '@apps/card/hooks/useCardPreviewWithPhoto';
import { CardPreviewWithPhotoProvider } from '@apps/card/contexts/CardPreviewWithPhotoContext';
import { CustomPhotoPreviewButton } from '@shared/components/CustomPhotoPreviewButton/CustomPhotoPreviewButton';
import { PhotoUploader } from '@apps/admin/components/PhotoUploader/PhotoUploader.lazy';
import { PICKER_TYPES } from '@apps/admin/components/PhotoPickerDialog/PhotoPickerDialog.types';
import { useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags/featureFlags.provider';
import type { GalleryPhotoPreviewProps } from './GalleryPhotoPreview.types';

export const GalleryPhotoPreview = ({ eventId, eventHandle, children }: GalleryPhotoPreviewProps) => {
  const { tq } = useTranslation('stationery');
  const [previewWithPhotoEnabled] = useIsFeatureEnabled('printPreviewWithPhotoCards');

  const {
    uploadedPreviewImages,
    selectedItemIds,
    handleCustomPhotoUploadCTAClick,
    handlePhotoSelect,
    handleClearAllPhotos,
    isOpenPickerDialog,
    onOpenPickerDialog,
    onClosePickerDialog,
    media,
    isLoadingEventMedia,
  } = useCardPreviewWithPhoto({ enabled: previewWithPhotoEnabled });

  const previewSecondaryFooterAction = useMemo(
    () => (uploadedPreviewImages.length > 0 ? { label: uploadedPreviewImages.length > 1 ? tq`Clear Photos` : tq`Clear Photo`, onClick: handleClearAllPhotos } : undefined),
    [uploadedPreviewImages.length, handleClearAllPhotos, tq],
  );

  const previewAction =
    eventHandle && previewWithPhotoEnabled ? (
      <CustomPhotoPreviewButton
        size="sm"
        uploadedPreviewImages={uploadedPreviewImages}
        handleCustomPhotoUploadCTAClick={handleCustomPhotoUploadCTAClick}
      />
    ) : undefined;

  return (
    <CardPreviewWithPhotoProvider
      photos={previewWithPhotoEnabled ? uploadedPreviewImages : []}
      isEnabled={previewWithPhotoEnabled}
    >
      {children(previewAction)}
      {previewWithPhotoEnabled && (
        <PhotoUploader
          isOpenPickerDialog={isOpenPickerDialog}
          onOpenPickerDialog={onOpenPickerDialog}
          onClosePickerDialog={onClosePickerDialog}
          containerId={eventId || ''}
          eventId={eventId || ''}
          isFetching={isLoadingEventMedia}
          media={media}
          showUploaderEntryPointCTA={false}
          showCount={false}
          pickerType={PICKER_TYPES.PICK_FROM_ALL_PHOTOS}
          onPhotoSelect={handlePhotoSelect}
          maxCount={3}
          minCount={1}
          useCase={{ area: 'admin', subArea: 'stationery' }}
          selectedItems={selectedItemIds}
          secondaryFooterAction={previewSecondaryFooterAction}
        />
      )}
    </CardPreviewWithPhotoProvider>
  );
};
